import { useEffect, useState } from "react";
import { dtoToRgObject } from "@project-rouge/rg-core";
import { Decompress } from "src/utils/Decompress";
import { ThumbnailRenderer } from "src/types/ThumbnailRenderer";
import type { RgWorld, RgWorldDto } from "src/types/RgCorePackage";
import type { GeneratedItem } from "src/types/GeneratedItem";

const cache = new Map<string, string>();
let queue: Promise<unknown> = Promise.resolve();

async function renderIso(item: GeneratedItem): Promise<string | undefined> {
  const key = item.rgWorldHashKey;
  if (cache.has(key)) return cache.get(key);
  const run = queue.then(async () => {
    if (cache.has(key)) return cache.get(key);
    const rgWorld = dtoToRgObject<RgWorld>(Decompress<RgWorldDto>(item.rgWorldZip));
    const url = await ThumbnailRenderer.renderImage(rgWorld, { size: 240 });
    cache.set(key, url);
    return url;
  });
  queue = run.catch(() => {});
  return run;
}

/**
 * Design thumbnail. "3d" renders an isometric view client-side (Studio only);
 * "2d" uses the shared top-down `item.thumbnail` as-is (no extra render).
 */
export function DesignThumb({
  item,
  className,
  mode = "3d",
}: {
  item: GeneratedItem;
  className?: string;
  mode?: "2d" | "3d";
}) {
  const [url, setUrl] = useState<string | undefined>(() => cache.get(item.rgWorldHashKey));
  useEffect(() => {
    if (mode !== "3d") return;
    let cancelled = false;
    renderIso(item)
      .then((u) => !cancelled && u && setUrl(u))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [item, mode]);
  // 3D render isn't ready yet (and isn't cached) — show a skeleton instead of
  // flashing the 2D thumbnail first.
  if (mode === "3d" && !url) {
    return (
      <span className={(className ?? "") + " animate-pulse"} style={{ background: "#ece8ff" }} />
    );
  }
  const src = mode === "2d" ? item.thumbnail : url;
  return <img src={src} alt="" className={className} />;
}
